import {useEffect, useMemo, useState} from 'react';
import type {MatrixEvent, Room} from 'matrix-js-sdk';
import {Phone, PhoneMissed, Users, Video} from 'lucide-react';
import {useIntl} from 'react-intl';
import type {CallIntent} from '@phonehq/rtc-core';
import {matrixService} from '../../../services/matrixService';
import {latestRtcNotificationId, rtcNotificationIntent} from '../../../utilis/rtcNotification';
import {useRoomCallSummary, useRtcFeatures} from '../hooks/useGroupCall';
import {useRingOutcome} from '../hooks/useRingOutcome';
import {useFetchGroupCallSessionsQuery} from '../services/groupCallHistoryService';
import {answeredLengthMs, sessionNearest} from '../services/GroupCallHistoryPresentation';
import {formatCallDurationMs} from '../../../utilis/callRow';
import {groupCallMessages as m} from '../messages';
import {countCallPeople} from '../services/callPeople';
import {isOneToOneCallRoom} from '../services/callRoomShape';
import type {RingOutcome} from '../services/ringOutcomes';
import {openRoomCallLobby} from './groupCallActions';
import '../groupCalls.css';

export interface GroupCallTimelineRowViewProps {
    intent: CallIntent;
    oneToOne: boolean;
    starterName: string | null;
    startedAt: number;
    participants: number | null;
    outcome?: Exclude<RingOutcome, 'answered'>;
    durationMs?: number;
    videoAvailable?: boolean;
    onJoin?: () => void;
}

const titleMessage = (intent: CallIntent, oneToOne: boolean) => {
    if (intent === 'video') {
        return oneToOne ? m.timelineTitleVideo : m.timelineTitleGroupVideo;
    }
    return oneToOne ? m.timelineTitleAudio : m.timelineTitleGroupAudio;
};

export const GroupCallTimelineRowView = ({intent, oneToOne, starterName, startedAt, participants, outcome, durationMs, videoAvailable = true, onJoin}: GroupCallTimelineRowViewProps) => {
    const intl = useIntl();
    const live = participants !== null;
    const missed = !live && outcome === 'missed';
    const state = live ? 'in-progress' : missed ? 'missed' : outcome === 'declined' ? 'declined' : 'ended';
    const Icon = missed ? PhoneMissed : intent === 'video' ? Video : oneToOne ? Phone : Users;
    const JoinIcon = intent === 'video' && videoAvailable ? Video : Phone;
    const time = intl.formatTime(startedAt, {hour: '2-digit', minute: '2-digit', hourCycle: 'h23'});
    const title = intl.formatMessage(titleMessage(intent, oneToOne));
    const startedLine = starterName === null
        ? intl.formatMessage(m.timelineStartedByYou, {time})
        : intl.formatMessage(m.timelineStartedBy, {name: starterName, time});
    const statusLine = live
        ? intl.formatMessage(m.timelineInProgress, {count: participants})
        : missed
            ? intl.formatMessage(oneToOne ? m.timelineMissedCall : m.timelineMissed)
            : outcome === 'declined'
                ? intl.formatMessage(m.timelineDeclined)
                : durationMs
                    ? intl.formatMessage(m.timelineEndedDuration, {duration: formatCallDurationMs(durationMs)})
                    : intl.formatMessage(m.timelineEnded);

    return (
        <div
            data-testid="chat.message.group-call-notification"
            data-call-intent={intent}
            data-call-state={state}
            className="phq-call-ui flex px-2 py-2"
        >
            <div
                className={`inline-flex max-w-[420px] items-center gap-3 rounded-xl border px-4 py-3.5 ${
                    live ? 'border-emerald-100 bg-emerald-50' : missed ? 'border-red-100 bg-red-50' : 'border-transparent bg-zinc-100'
                }`}
            >
                <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                        live ? 'bg-emerald-100' : missed ? 'bg-red-100' : 'bg-zinc-50'
                    }`}
                >
                    <Icon size={18} aria-hidden="true" className={live ? 'text-emerald-600' : missed ? 'text-red-600' : 'text-zinc-500'}/>
                </span>

                <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium leading-[18px] text-ink">{title}</span>
                    <span className="mt-0.5 block text-[13px] leading-[18px] text-zinc-500">{startedLine}</span>
                    <span
                        data-testid="chat.message.group-call-status"
                        className={`block text-[13px] leading-[18px] ${live ? 'font-medium text-emerald-700' : missed ? 'font-medium text-red-600' : 'text-zinc-400'}`}
                    >
                        {statusLine}
                    </span>
                </span>

                {live && onJoin && (
                    <button
                        type="button"
                        data-testid="chat.message.group-call-join"
                        onClick={onJoin}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500"
                    >
                        <JoinIcon size={14} aria-hidden="true"/>
                        {intl.formatMessage(m.timelineJoin)}
                    </button>
                )}
            </div>
        </div>
    );
};

const useLatestRtcNotificationId = (roomId: string): string | null => {
    const [revision, setRevision] = useState(0);

    useEffect(() => {
        const onNotification = ({room}: {room: Room}) => {
            if (room.roomId === roomId) {
                setRevision(value => value + 1);
            }
        };
        matrixService.on('rtcNotification', onNotification);
        return () => {
            matrixService.off('rtcNotification', onNotification);
        };
    }, [roomId]);

    return useMemo(() => {
        void revision;
        return latestRtcNotificationId(matrixService.getRoom(roomId)?.getLiveTimeline().getEvents() ?? []);
    }, [roomId, revision]);
};

const displayNameOf = (room: Room | null | undefined, userId: string): string => {
    const name = room?.getMember(userId)?.rawDisplayName;
    return name && name !== userId ? name : userId.split(':')[0].replace('@', '');
};

const SESSIONS_PER_ROOM = 50;
const SESSION_REFRESH_MS = 10_000;
const SESSION_REFRESH_ATTEMPTS = 6;

export interface GroupCallTimelineRowProps {
    message: MatrixEvent;
    roomId: string;
    getCurrentUserId: () => string | null;
}

export const GroupCallTimelineRow = ({message, roomId, getCurrentUserId}: GroupCallTimelineRowProps) => {
    const summary = useRoomCallSummary(roomId);
    const features = useRtcFeatures();
    const latestId = useLatestRtcNotificationId(roomId);
    const sender = message.getSender() ?? '';
    const people = summary ? countCallPeople(summary.userIds) : 0;
    const isLatest = !!latestId && latestId === message.getId();
    const participants = summary && people > 0 && isLatest ? people : null;
    const joinable = participants !== null && summary && !summary.includesMe;
    const startedByMe = !!sender && sender === getCurrentUserId();
    const wasRung = message.getContent()?.['notification_type'] === 'ring';
    const outcome = useRingOutcome(roomId, message.getId(), participants === null && !startedByMe && wasRung && !message.isRedacted());
    const ended = participants === null && outcome !== 'missed' && outcome !== 'declined' && !message.isRedacted();
    const {data: sessions, refetch} = useFetchGroupCallSessionsQuery({roomId, size: SESSIONS_PER_ROOM}, {skip: !ended});
    const session = ended && sessions ? sessionNearest(sessions.sessions, message.getTs()) : undefined;
    const awaitingEnd = isLatest && ended && !!sessions && !session?.endedAt;

    useEffect(() => {
        if (!awaitingEnd) {
            return;
        }
        let attempts = 0;
        const timer = window.setInterval(() => {
            attempts += 1;
            void refetch();
            if (attempts >= SESSION_REFRESH_ATTEMPTS) {
                window.clearInterval(timer);
            }
        }, SESSION_REFRESH_MS);
        return () => window.clearInterval(timer);
    }, [awaitingEnd, refetch]);

    if (message.isRedacted()) {
        return null;
    }

    const room = matrixService.getRoom(roomId);

    return (
        <GroupCallTimelineRowView
            intent={rtcNotificationIntent(message)}
            oneToOne={!!room && isOneToOneCallRoom(room, matrixService.isDirectMessageRoom(room))}
            starterName={startedByMe ? null : displayNameOf(room, sender)}
            startedAt={message.getTs()}
            participants={participants}
            outcome={outcome === 'missed' || outcome === 'declined' ? outcome : undefined}
            durationMs={session ? answeredLengthMs(session) : undefined}
            videoAvailable={features.videoCalls}
            onJoin={joinable ? () => openRoomCallLobby(roomId, features.videoCalls ? (summary.intent ?? 'audio') : 'audio', 'chat') : undefined}
        />
    );
};
